import UnisuggestApiClient from '../api/unisuggestApiClient';

/** デフォルトサジェスト設定 */
const defaultSuggestConfig = {
  delay: 500,
  api: {
    loginId: '',
    rows: 30,
    category: 4,
    modelnumber: 6,
    itemnumber: 6,
    productname: 5,
    maker: 5,
    campaigntag: 4,
  },
};

/**
 * サジェスト設定が妥当な構成かどうかチェックする。
 *
 * @param {InputSuggestConfig} config サジェスト設定
 * @returns {void}
 * @throws {Error} サジェスト設定が妥当な構成ではない場合
 */
export function validateConfig(config) {
  if (!(config.type === 'pc' || config.type === 'sp')) {
    throw new Error("'type'の設定が不適切です。");
  }

  /** @type {(keyof InputSuggestConfig)[]} */
  const checkConfigKeys = ['triggerTarget', 'wordSuggestRoot', 'historySuggestRoot', 'searchAppBaseUrl']
  checkConfigKeys.forEach(key => {
    if (typeof config[key] !== 'string') {
      throw new Error(`'${key}'の設定が不適切です。`);
    }
  });

  if (typeof config.api !== 'object') {
    throw new Error("'api'の設定が不適切です。");
  }

  /** @type {(keyof InputSuggestApiConfig)[]} */
  const checkApiConfigKeys = ['baseUrl', 'serviceName', 'showCd']
  checkApiConfigKeys.forEach(key => {
    if (typeof config.api[key] !== 'string') {
      throw new Error(`'api.${key}'の設定が不適切です。`);
    }
  });
}

/**
 * サジェスト設定とデフォルト設定をマージする。
 *
 * @param {InputSuggestConfig} config
 * @returns {SuggestConfig}
 */
export function mergeDefaultConfig(config) {
  return $.extend(true, {}, defaultSuggestConfig, config);
}

/**
 * ユニサジェストAPIのリクエストパラメータ `uid` を取得する。
 *
 * @returns {string}
 */
function getUID() {
  const value = document.cookie.match(
    new RegExp('__ukwlgck\=([^\;]*)\;*')
  )?.[1] ?? '';
  const uid = value.split('_')?.[0] ?? '';
  return uid;
}

/**
 * ユニサジェストAPIのリクエストパラメータにユーザ識別用のパラメータを追加する。
 *
 * @param {Partial<UniSuggestApiRequest>} params リクエストパラメータ
 * @param {SuggestConfig} config サジェスト設定
 */
function addUserParams(params, config) {
  const uid = getUID();
  if (uid) {
    params.uid = uid;
  }
  params.loginid = config.api.loginId;
}

/** @type {UnisuggestApiClient} */
let suggestApiClient;

/**
 * ユニサジェストAPIクライアントを取得する。
 *
 * @param {SuggestConfig} config サジェスト設定
 * @returns {UnisuggestApiClient}
 */
function getApiClient(config) {
  return suggestApiClient ||= new UnisuggestApiClient(config.api.baseUrl, config.api.serviceName);
}

/**
 * ユニサジェストAPIを呼び出し、サジェストの検索を行う。
 *
 * @param {string} keyword 検索キーワード
 * @param {SuggestConfig} config サジェスト設定
 * @returns {Promise<UniSuggestApiResponse|false>}
 */
export async function executeSuggestSearchByKeyword(keyword, config) {
  /** @type {Partial<UniSuggestApiRequest>} */
  const commonParams = {
    rows: config.api.rows,
    dr_category: config.api.category,
    dr_modelnumber: config.api.modelnumber,
    dr_itemnumber: config.api.itemnumber,
    dr_productname: config.api.productname,
    dr_maker: config.api.maker,
    dr_campaigntag: config.api.campaigntag,
    history: 'on',
    fq_show_cd: config.api.showCd,
  };
  addUserParams(commonParams, config);
  const response = await getApiClient(config).suggestSearch({
    ...commonParams,
    kw: keyword,
  });
  return response;
}

/**
 * ユニサジェストAPIを呼び出し、履歴サジェストの取得を行う。
 *
 * @param {SuggestConfig} config サジェスト設定
 * @returns {Promise<UniSuggestApiResponse|false>}
 */
export async function executeFetchHistorySuggest(config) {
  /** @type {UniSuggestApiRequest} */
  const params = {
    kw: '',
    history: 'on',
    fq_show_cd: config.api.showCd,
  };
  addUserParams(params, config);
  const response = await getApiClient(config).suggestSearch(params);
  return response;
}

/**
 * ユニサジェストAPIを呼び出し、検索履歴の削除を行う。
 *
 * @param {SuggestConfig} config サジェスト設定
 * @returns {Promise<UniSuggestHistoryDeleteApiResponse|false>}
 */
export async function executeDeleteSearchHistory(config) {
  /** @type {UniSuggestHistoryDeleteApiRequest} */
  const params = {};
  addUserParams(params, config);
  const response = await getApiClient(config).historyDelete(params);
  return response;
}
